import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Menu, MenuItem, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import US from "country-flag-icons/react/1x1/US";
import BR from "country-flag-icons/react/1x1/BR";
import AR from "country-flag-icons/react/1x1/AR";
import styled from "styled-components";

// Minimal modern dropdown styles
const useMenuStyles = makeStyles((theme) => ({
  paper: {
    marginTop: 8,
    minWidth: 140,
    borderRadius: 12,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.06)",
    padding: "6px 0",
  },
  menuItem: {
    fontSize: 14,
    fontWeight: 400,
    padding: "10px 16px",
    margin: "0 4px",
    borderRadius: 8,
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
    "&$selected": {
      backgroundColor: `${theme.palette.primary.main}14`,
      fontWeight: 500,
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}1F`,
      },
    },
  },
  selected: {},
}));

// Circular flags in the dropdown menu (matches the main floating button style)
const MenuFlagWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  margin-right: 10px;
  border-radius: 50%;
  overflow: hidden;

  & svg {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/**
 * Fixed-position circular flag button in top-right corner.
 * Click opens a menu to switch between English and Portuguese.
 */
const FloatingButton = styled(IconButton)`
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 1300;
  width: 26px;
  height: 26px;
  padding: 0;
  background: transparent;

  &:hover {
    background: transparent;
  }
`;

// 1x1 flags are square; border-radius 50% makes them circular. Shadow directly on flag edge.
const CircularFlagWrapper = styled.span`
  display: block;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
  transition: box-shadow 0.2s ease;

  ${FloatingButton}:hover & {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  }

  & svg {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

function FloatingLanguageSwitcher() {
  const { i18n, t } = useTranslation("common");
  const currentLang = i18n.language?.split("-")[0] || "en";
  const [anchorEl, setAnchorEl] = useState(null);
  const menuClasses = useMenuStyles();

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang) => {
    if (lang !== currentLang) {
      i18n.changeLanguage(lang);
    }
    handleClose();
  };

  const FlagIcon = currentLang === "pt" ? BR : currentLang === "es" ? AR : US;
  const flagTitle = currentLang === "pt" ? "Português" : currentLang === "es" ? "Español" : "English";

  return (
    <>
      <Tooltip title={t("language")} arrow placement="left">
        <FloatingButton
          onClick={handleOpen}
          aria-label={t("language")}
          aria-haspopup="true"
          aria-controls="language-menu"
        >
          <CircularFlagWrapper>
            <FlagIcon title={flagTitle} />
          </CircularFlagWrapper>
        </FloatingButton>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        getContentAnchorEl={null}
        PaperProps={{
          className: menuClasses.paper,
        }}
        MenuListProps={{
          disablePadding: true,
        }}
      >
        <MenuItem
          onClick={() => handleLanguageChange("en")}
          selected={currentLang === "en"}
          classes={{ root: menuClasses.menuItem, selected: menuClasses.selected }}
          disableRipple
        >
          <MenuFlagWrapper>
            <US />
          </MenuFlagWrapper>
          {t("english")}
        </MenuItem>
        <MenuItem
          onClick={() => handleLanguageChange("es")}
          selected={currentLang === "es"}
          classes={{ root: menuClasses.menuItem, selected: menuClasses.selected }}
          disableRipple
        >
          <MenuFlagWrapper>
            <AR />
          </MenuFlagWrapper>
          {t("spanish")}
        </MenuItem>
        <MenuItem
          onClick={() => handleLanguageChange("pt")}
          selected={currentLang === "pt"}
          classes={{ root: menuClasses.menuItem, selected: menuClasses.selected }}
          disableRipple
        >
          <MenuFlagWrapper>
            <BR />
          </MenuFlagWrapper>
          {t("portuguese")}
        </MenuItem>
      </Menu>
    </>
  );
}

export default FloatingLanguageSwitcher;
